import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  annualDuesRequestSchema,
  type AnnualDuesPreviewView,
  type AnnualDuesRequest,
  type AnnualDuesView,
  type BillingAccountDetailView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate } from '@stm/i18n';
import { apiRequest } from './api';
import { formatCalendarDate } from './athletes';
import { formatMoney, parseAmountInput, paymentMethodKeys } from './billing';
import { errorKeyOf, type ErrorKey } from './BillingForms';

export function AnnualDuesPanel({
  locale,
  base,
  account,
  csrfToken,
  onPosted,
}: {
  locale: SupportedLocale;
  base: string;
  account: BillingAccountDetailView;
  csrfToken: string;
  onPosted: () => Promise<void>;
}): React.JSX.Element {
  const path = `${base}/accounts/${account.id}/annual-dues`;
  const [yearText, setYearText] = useState(() => String(new Date().getFullYear()));
  const [amountText, setAmountText] = useState('');
  const [description, setDescription] = useState('');
  const [installments, setInstallments] = useState<1 | 12>(12);
  const [dueDay, setDueDay] = useState('10');
  const [dueMonth, setDueMonth] = useState('1');
  const [view, setView] = useState<AnnualDuesView | null>(null);
  const [preview, setPreview] = useState<{
    signature: string;
    request: AnnualDuesRequest;
    view: AnnualDuesPreviewView;
  } | null>(null);
  const [error, setError] = useState<ErrorKey | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const postingKey = useRef<{ signature: string; key: string } | null>(null);
  const validYear =
    /^\d{4}$/.test(yearText) && Number(yearText) >= 2000 && Number(yearText) <= 2100;
  const parsed = annualDuesRequestSchema.safeParse({
    year: Number(yearText),
    amountMinor: parseAmountInput(amountText),
    description,
    installments,
    dueDay: Number(dueDay),
    dueMonth: installments === 12 ? 1 : Number(dueMonth),
  });
  const request = parsed.success ? parsed.data : null;
  const signature = request === null ? '' : JSON.stringify(request);
  const currentPreview = preview?.signature === signature ? preview : null;

  useEffect(() => {
    let active = true;
    setView(null);
    setError(null);
    setPreview(null);
    if (validYear) {
      void apiRequest<AnnualDuesView>(`${path}?year=${yearText}`)
        .then((result) => {
          if (active) setView(result);
        })
        .catch((cause: unknown) => {
          if (active) setError(errorKeyOf(cause));
        });
    }
    return () => {
      active = false;
    };
  }, [path, yearText, validYear, account]);

  async function previewDues(): Promise<void> {
    if (request === null || busy) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    setPreview(null);
    try {
      const result = await apiRequest<AnnualDuesPreviewView>(`${path}/preview`, {
        method: 'POST',
        csrfToken,
        body: request,
      });
      setPreview({ signature, request, view: result });
    } catch (cause) {
      setError(errorKeyOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function postDues(): Promise<void> {
    if (currentPreview === null || busy) return;
    if (postingKey.current?.signature !== signature)
      postingKey.current = { signature, key: crypto.randomUUID() };
    setBusy(true);
    setError(null);
    try {
      const result = await apiRequest<AnnualDuesView>(path, {
        method: 'POST',
        csrfToken,
        body: currentPreview.request,
        idempotencyKey: postingKey.current.key,
      });
      setView(result);
      setPreview(null);
      setSaved(true);
      postingKey.current = null;
      await onPosted();
    } catch (cause) {
      setError(errorKeyOf(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box component="section" sx={{ mb: 3 }} aria-label={translate(locale, 'billing.annual.title')}>
      <Typography variant="h6" component="h3" gutterBottom>
        {translate(locale, 'billing.annual.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'billing.annual.help')}
      </Typography>
      <TextField
        size="small"
        type="number"
        label={translate(locale, 'billing.annual.year')}
        value={yearText}
        disabled={busy}
        onChange={(event) => {
          setYearText(event.target.value);
          setSaved(false);
        }}
        error={!validYear}
        slotProps={{ htmlInput: { min: 2000, max: 2100 } }}
        sx={{ mb: 2 }}
      />
      {error !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, error)}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, 'billing.annual.saved')}
        </Alert>
      )}
      {view !== null && view.charges.length > 0 && (
        <>
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {(
              [
                ['billing.annual.amount', view.amountMinor],
                ['billing.annual.credited', view.creditedMinor],
                ['billing.annual.paid', view.paidMinor],
                ['billing.annual.remaining', view.remainingMinor],
              ] as const
            ).map(([key, amount]) => (
              <Paper key={key} variant="outlined" sx={{ padding: 1.5, flex: '1 1 150px' }}>
                <Typography variant="body2">{translate(locale, key)}</Typography>
                <Typography variant="h6" component="p">
                  {formatMoney(amount, account.currency, locale)}
                </Typography>
              </Paper>
            ))}
          </Stack>
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0 }}>
            {view.charges.map((charge) => (
              <Paper component="li" key={charge.id} variant="outlined" sx={{ padding: 1.5 }}>
                <Typography>
                  {charge.description} · {formatCalendarDate(charge.dueOn, locale)}
                </Typography>
                <Typography variant="body2">
                  {translate(locale, 'billing.col.amount')}:{' '}
                  {formatMoney(charge.amountMinor, account.currency, locale)} ·{' '}
                  {translate(locale, 'billing.annual.paid')}:{' '}
                  {formatMoney(charge.allocatedMinor, account.currency, locale)} ·{' '}
                  {translate(locale, 'billing.annual.credited')}:{' '}
                  {formatMoney(charge.creditedMinor, account.currency, locale)} ·{' '}
                  {translate(locale, 'billing.annual.remaining')}:{' '}
                  {formatMoney(charge.outstandingMinor, account.currency, locale)}
                </Typography>
              </Paper>
            ))}
          </Stack>
          <Typography variant="subtitle1" component="h4">
            {translate(locale, 'billing.annual.payments')}
          </Typography>
          {view.payments.length === 0 && (
            <Typography color="text.secondary">
              {translate(locale, 'billing.payments.empty')}
            </Typography>
          )}
          <Stack component="ul" spacing={1} sx={{ listStyle: 'none', padding: 0 }}>
            {view.payments.map((payment) => (
              <Paper component="li" key={payment.id} variant="outlined" sx={{ padding: 1.5 }}>
                <Typography>
                  {payment.payerReference ?? account.label} ·{' '}
                  {formatCalendarDate(payment.paidOn, locale)} ·{' '}
                  {translate(locale, paymentMethodKeys[payment.method])}
                </Typography>
                <Typography variant="body2">
                  {payment.reference} · {translate(locale, 'billing.col.amount')}:{' '}
                  {formatMoney(payment.amountMinor, account.currency, locale)} ·{' '}
                  {translate(locale, 'billing.annual.applied')}:{' '}
                  {formatMoney(payment.appliedMinor, account.currency, locale)}
                </Typography>
              </Paper>
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {translate(locale, 'billing.annual.paymentHelp')}
          </Typography>
        </>
      )}
      {view !== null && view.charges.length === 0 && (
        <Typography sx={{ mb: 2 }}>{translate(locale, 'billing.annual.empty')}</Typography>
      )}
      {account.status === 'active' && view !== null && view.charges.length === 0 && (
        <Stack
          component="form"
          spacing={2}
          sx={{ maxWidth: 560 }}
          onSubmit={(event) => {
            event.preventDefault();
            void previewDues();
          }}
        >
          <TextField
            required
            size="small"
            label={translate(locale, 'billing.charges.post.description')}
            value={description}
            disabled={busy}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            slotProps={{ htmlInput: { maxLength: 160 } }}
          />
          <TextField
            required
            size="small"
            label={`${translate(locale, 'billing.annual.amount')} (${account.currency})`}
            value={amountText}
            disabled={busy}
            error={amountText !== '' && parseAmountInput(amountText) === null}
            onChange={(event) => {
              setAmountText(event.target.value);
            }}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'billing.annual.installments')}
            value={installments}
            disabled={busy}
            onChange={(event) => {
              setInstallments(Number(event.target.value) === 1 ? 1 : 12);
            }}
          >
            <MenuItem value={1}>{translate(locale, 'billing.annual.once')}</MenuItem>
            <MenuItem value={12}>{translate(locale, 'billing.annual.monthly')}</MenuItem>
          </TextField>
          {installments === 1 && (
            <TextField
              required
              size="small"
              type="number"
              label={translate(locale, 'billing.annual.dueMonth')}
              value={dueMonth}
              disabled={busy}
              onChange={(event) => {
                setDueMonth(event.target.value);
              }}
              slotProps={{ htmlInput: { min: 1, max: 12 } }}
            />
          )}
          <TextField
            required
            size="small"
            type="number"
            label={translate(locale, 'billing.fees.plan.dueDay')}
            helperText={translate(locale, 'billing.fees.plan.dueDayHelp')}
            value={dueDay}
            disabled={busy}
            onChange={(event) => {
              setDueDay(event.target.value);
            }}
            slotProps={{ htmlInput: { min: 1, max: 31 } }}
          />
          <Button variant="outlined" type="submit" disabled={request === null || busy}>
            {translate(locale, 'billing.annual.preview')}
          </Button>
        </Stack>
      )}
      {currentPreview !== null && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info">{translate(locale, 'billing.annual.confirmHelp')}</Alert>
          <Stack component="ul" spacing={1} sx={{ pl: 3 }}>
            {currentPreview.view.installments.map((line) => (
              <Typography component="li" key={line.billingPeriod}>
                {formatCalendarDate(line.dueOn, locale)} ·{' '}
                {formatMoney(line.amountMinor, account.currency, locale)}
              </Typography>
            ))}
          </Stack>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => {
              void postDues();
            }}
          >
            {translate(locale, 'billing.annual.confirm')}
          </Button>
        </Box>
      )}
    </Box>
  );
}
